import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { countries } from 'countries-list';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { InputNumberInputEvent } from 'primeng/inputnumber';
import { SelectChangeEvent } from 'primeng/select';
import { map, of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

// Do something similar like this
// https://baseweb.design/blog/phone-input/

@Component({
  selector: 'app-formly-phone',
  template: `
    <div class="flex items-center gap-2 flex-nowrap">
      <p-select
        [options]="countries"
        optionLabel="name"
        appendTo="body"
        [filter]="true"
        filterBy="name,"
        dataKey="code"
        [formControl]="countryControl"
        (onChange)="onCountryChange($event)"
        class="w-30"
      >
        <ng-template #selectedItem let-selectedOption>
          <div
            class="flex items-center gap-2 w-52 h1-[27px] selected-country-label"
            *ngIf="selectedCountry"
          >
            <span
              class="flag fi fi-{{
                selectedCountry.code.toLowerCase()
              }} mr-1 ml-1"
            ></span>
            <div class="hidden text-gray-400">
              +{{ selectedCountry.phone[0] }}
            </div>
          </div>
        </ng-template>

        <ng-template let-country #item>
          <div class="flex items-center gap-3">
            <span
              class="w-48 text-[18px] fi fi-{{
                country.code.toLowerCase()
              }}  mr-2"
            ></span>
            <div>{{ country.name }}</div>
            <div class="text-gray-400 justify-self-end">
              +{{ country.phone[0] }}
            </div>
          </div>
        </ng-template>
      </p-select>

      <div class="relative w-full phone-input">
        <div class="phone-prefix absolute z-10  text-base text-gray-800">
          +{{ selectedCountry?.phone[0] ?? '' }}
        </div>
        <p-inputnumber
          [formControl]="phoneNumberControl"
          [useGrouping]="false"
          [format]="false"
          class="phone-input w-full h-full p-0"
          (onInput)="onPhoneNumberChange($event)"
        />
      </div>
    </div>
  `,
  styles: [
    `
      .p-select {
        width: 80px !important;
        height: 45px !important;
      }

      .phone-input p-inputnumber {
        height: 45px !important;
      }

      .phone-input ::ng-deep input {
        height: 45px !important;
        padding-left: 60px !important;
      }

      .flag {
        font-size: 18px;
      }

      a {
        right: 14px;
      }

      .phone-prefix {
        top: 11px;
        left: 15px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyPhoneComponent extends FieldType {
  countryControl: FormControl = new FormControl('');
  phoneNumberControl: FormControl = new FormControl(null);
  countries: any[] = [];

  phoneNumber: string | undefined;
  selectedCountry: any | null = null;
  phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();

  get control(): FormControl {
    return this.formControl as FormControl;
  }

  private apiKey =
    '9e4188c583b3d3e9.59c42602fb42426520760ec6809cd6460d35cc0002e2fbee392d2251385d960d';

  constructor(private http: HttpClient) {
    super();
  }

  getCountryCodeBaseOnIp(): Observable<any> {
    const apiUrl = 'https://ipwho.is';
    return this.http.get<any>(apiUrl).pipe(
      map(response => {
        return response?.country_code;
      })
    );
  }

  ngOnInit() {
    this.countries = Object.entries(countries)
      .map(([code, data]) => ({
        code,
        ...data,
      }))
      .sort((a, b) => {
        // Put US first
        if (a.code === 'US') return -1;
        if (b.code === 'US') return 1;
        return a.name.localeCompare(b.name);
      });

    // Set up custom validator
    this.formControl.addValidators(this.validatePhone.bind(this));

    // Always start with a concrete country (US) so the field is never in a
    // "no country selected" state. Geolocation only refines this default.
    this.selectedCountry = this.countries[0];
    this.countryControl.setValue(this.selectedCountry);

    // Keep the combined value in sync whenever either part changes, regardless
    // of which one the user (or async geolocation) sets first.
    this.countryControl.valueChanges.subscribe(country => {
      this.selectedCountry = country ?? this.countries[0];
      this.setFormControlValue();
    });
    this.phoneNumberControl.valueChanges.subscribe(() =>
      this.setFormControlValue()
    );

    if (this.formControl.value) {
      this._setPhone();
    } else {
      this.getCountryCodeBaseOnIp().subscribe({
        next: countryCode => {
          const match = this.countries.find(
            country => country.code === countryCode
          );
          // Only override the US default when geolocation gives a real match.
          if (match) {
            this.countryControl.setValue(match);
          }
        },
        error: error => {
          // Keep the US default already set above.
          console.error('Error fetching country code:', error);
        },
      });
    }
  }

  onCountryChange(event: SelectChangeEvent) {
    this.selectedCountry = event.value;
    this.setFormControlValue();
  }

  onPhoneNumberChange(event: InputNumberInputEvent) {
    this.setFormControlValue();
  }

  validatePhone() {
    if (!this.formControl.value || !this.selectedCountry) {
      return null; // Skip validation if no country is selected
    }
    try {
      const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
        this.formControl.value,
        this.selectedCountry.code
      );
      const isValid = this.phoneUtil.isValidNumberForRegion(
        phoneNumber,
        this.selectedCountry.code
      );
      return isValid ? null : { invalidPhoneNumber: true };
    } catch (error) {
      console.error('Error parsing phone number:', (error as any).message);
      return { invalidPhoneNumber: true };
    }
  }

  private setFormControlValue() {
    const country = this.countryControl.value ?? this.selectedCountry;
    const number = this.phoneNumberControl.value;

    if (!country || number === null || number === undefined || number === '') {
      this.formControl.setValue(null);
      this.formControl.updateValueAndValidity();
      return;
    }

    this.formControl.setValue(`+${country.phone[0]}${number}`);
    this.formControl.updateValueAndValidity();
  }

  private _setPhone() {
    try {
      // Parse the existing phone number in the control
      const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
        this.formControl.value,
        this.selectedCountry.code // Use the current country's code
      );

      const nationalNumber = this.phoneUtil.format(
        phoneNumber,
        PhoneNumberFormat.NATIONAL
      );

      const regionCode = this.phoneUtil.getRegionCodeForNumber(phoneNumber);
      this.selectedCountry = this.countries.find(
        country => country.code === regionCode
      );

      this.countryControl.setValue(this.selectedCountry);

      //phone
      this.phoneNumber = nationalNumber;
      this.phoneNumberControl.setValue(nationalNumber.replace(/\D/g, ''));
    } catch (error) {
      console.error('Error setting phone number:', error);
    }
  }

  private removeCountryPrefix(formattedNumber: string): string {
    const parts = formattedNumber.split(' ');
    parts.shift(); // Remove the first part, which is the country prefix (e.g., "+1")
    return parts.join(' '); // Join the remaining parts back into a single string
  }
}
